import type { ControllerRenderProps } from "react-hook-form";

import { ObservationAnswersList } from "src/features/observationsAdmin/containers";
import { Info } from "lucide-react";
import { useAdminUpsertQuestion } from "src/features/Admin/Observations/hooks/useAdminUpsertQuestion";

type AdminRadioBlockProps = {
	questionIndex: number;

	question: ControllerRenderProps<
		AdminObservationForm,
		`questions.${number}.question`
	>;

	note: ControllerRenderProps<AdminObservationForm, `questions.${number}.note`>;

	isActionRequired: ControllerRenderProps<
		AdminObservationForm,
		`questions.${number}.isActionRequired`
	>;

	isCorrectAnswerRequired: ControllerRenderProps<
		AdminObservationForm,
		`questions.${number}.isCorrectAnswerRequired`
	>;

	observationType?: IObservationType;

	errors?: {
		question?: string;
	};

	isSettingsDisabled?: boolean;

	onBlur?: () => void;
};

const AdminRadioBlock = ({
	question,
	questionIndex,
	note,
	isActionRequired,
	observationType,
	isCorrectAnswerRequired,
	isSettingsDisabled,
	errors,
	...props
}: AdminRadioBlockProps) => {
	const { upsertQuestion, isSubmittingQuestion, isUpdatingQuestion } =
		useAdminUpsertQuestion({ questionIndex });

	const isDisabled = isSubmittingQuestion || isUpdatingQuestion;
	const isLoading = isSubmittingQuestion || isUpdatingQuestion;

	const handleBlur = async (onBlur: () => void) => {
		try {
			await upsertQuestion();
		} catch (error) {
			console.error("Error creating/updating question", error);
		} finally {
			onBlur();
			props?.onBlur?.();
		}
	};

	return (
		<div>
			<h4 className="font-heading text-lg font-medium text-foreground mb-6">
				Radio button
			</h4>

			<div className="flex flex-col gap-4">
				<div>
					<div className="flex justify-between mb-1">
						<label className="text-sm font-medium text-zinc-600">
							Question
						</label>
						<span className="text-[13px] text-muted-foreground">Required</span>
					</div>

					<div className="relative">
						<input
							type="text"
							className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50"
							placeholder="Enter question text"
							name={question.name}
							onChange={question.onChange}
							onBlur={() => handleBlur(question.onBlur)}
							value={question.value}
							ref={question.ref}
							disabled={isDisabled}
							autoFocus={true}
						/>

						{isLoading && (
							<span className="absolute right-3 top-1/2 -translate-y-1/2 w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
						)}
					</div>

					{errors?.question && (
						<p className="text-destructive mt-1 text-xs flex items-center gap-1">
							<Info size={16} />
							{errors.question}
						</p>
					)}
				</div>

				<div>
					<label className="text-sm font-medium text-zinc-600 mb-1 block">
						Note
					</label>

					<input
						type="text"
						className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50"
						placeholder="Add any additional information to support this question"
						name={note.name}
						onChange={note.onChange}
						onBlur={() => handleBlur(note.onBlur)}
						value={note.value}
						ref={note.ref}
						disabled={isDisabled}
					/>
				</div>
			</div>

			<div className="mt-6">
				<ObservationAnswersList
					questionIndex={questionIndex}
					isSubmittingQuestion={isLoading}
				/>
			</div>

			<div className="pt-6 mt-6 border-t border-border">
				<p className="text-sm font-medium text-zinc-600 mb-4">Settings</p>

				<div>
					<label
						className={`flex items-center gap-2 ${isSettingsDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
					>
						<input
							type="checkbox"
							checked={Boolean(isActionRequired.value)}
							onChange={isActionRequired.onChange}
							disabled={isSettingsDisabled}
						/>
						Include an optional action box for this question.
					</label>

					{observationType === "GREAT_CONVERSATION" && (
						<label
							className={`flex items-center gap-2 mt-4 ${isSettingsDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
						>
							<input
								type="checkbox"
								checked={Boolean(isCorrectAnswerRequired.value)}
								onChange={isCorrectAnswerRequired.onChange}
								disabled={isSettingsDisabled}
							/>
							Show options to select whether answers are correct or incorrect.
						</label>
					)}
				</div>
			</div>
		</div>
	);
};

export default AdminRadioBlock;
